import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  TextField,
  Typography,
  Paper,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlock } from '@fortawesome/free-solid-svg-icons/faUnlock';
import { faCircleQuestion, faLock } from '@fortawesome/free-solid-svg-icons';
import { PagesContext, PageSetting } from '../../../../contexts/PagesContext';

import SwitchButton from '../../../Buttons/SwitchButton';

interface ChangePasswordProps {
  pageSettings: PageSetting;
  isSwitchOn: boolean;
  toggleSaveButton: (value: boolean) => void;
  setIsSwitchOn: (value: boolean) => void;
  onPasswordChange: (password: string) => void;
  onDescriptionTextChange: (password: string) => void;
}

const PasswordProtection: React.FC<ChangePasswordProps> = ({
  onPasswordChange,
  onDescriptionTextChange,
  pageSettings,
  isSwitchOn,
  setIsSwitchOn,
  toggleSaveButton,
}) => {
  const switchRef = useRef<HTMLInputElement>(null);
  const { contextErrors } = useContext(PagesContext);

  const [initialPasswordState, _ps] = useState(isSwitchOn);

  const [newPassword, setNewPassword] = React.useState(pageSettings.password ?? '');
  const [newDescriptionText, setNewDescriptionText] = React.useState(pageSettings.auth_page_description ?? '');

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
  };

  const handleDescriptionTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewDescriptionText(event.target.value);
  };

  const handleDomainSwitchClick = () => {
    if (switchRef.current) {
      if (!switchRef.current.checked) {
        setIsSwitchOn(false);
      } else {
        setIsSwitchOn(true);
      }
    }
  };

  useEffect(() => {
    onPasswordChange(newPassword);
    onDescriptionTextChange(newDescriptionText);
  }, [newPassword, newDescriptionText]);

  useEffect(() => {
    if (!newPassword && isSwitchOn) {
      toggleSaveButton(true);
      return;
    }

    toggleSaveButton(false);
  }, [toggleSaveButton, isSwitchOn, newPassword]);

  return (
    <>
      <Box>
        <h2 className="mb-2">パスワード保護</h2>
      </Box>
      <Box sx={{ my: 2 }}>
        ページをパスワードで保護します。正しいパスワードを入力したユーザーのみがページを閲覧できます。
      </Box>

      <Box color="red">
        パスワード保護機能については、
        設定後に公開又は再公開を行ってください。
      </Box>

      <hr className="mt-3" />

      <div className="d-flex align-content-center justify-content-around w-75 mb-3">
        <span className="text-bold">
          パスワード保護を利用しない
        </span>

        <span className="d-flex align-content-center flex-wrap">
          <SwitchButton
            id="is_password_protected"
            defaultChecked={initialPasswordState}
            onClick={handleDomainSwitchClick}
            inputRef={switchRef}
          />
        </span>

        <span>
          パスワード保護を利用する
        </span>
      </div>

      {isSwitchOn && (
        <Box>
          <hr />

          <Paper variant="outlined" sx={{ padding: 2, backgroundColor: '#f9f9f9', textAlign: 'center' }}>
            <FontAwesomeIcon icon={!initialPasswordState ? faUnlock : faLock} className="px-2" />
            {!initialPasswordState ? '現在このページはパスワード保護されていません' : '現在このページはパスワード保護されています'}
          </Paper>
          <Box mt={4} display="flex">
            <Box sx={{ width: '200px', fontWeight: 'bold' }}>
              パスワード

              <span
                style={{
                  color: '#bb0000',
                  fontSize: '80%',
                  marginLeft: '4px',
                  padding: '4px',
                  borderRadius: '4px',
                  backgroundColor: '#fff0f0',
                }}
              >
                必須
              </span>
            </Box>
            <TextField
              name="password"
              size="small"
              sx={{ width: '28%' }}
              type="password"
              placeholder="例：abcd123!@#"
              value={newPassword}
              onChange={handlePasswordChange}
              helperText="4文字〜16文字以下でご入力ください 半角英数字記号がご利用いただけます"
              variant="outlined"
              error={!!contextErrors?.password}
            />
          </Box>
          <hr className="mt-3" />
          <Box mt={4} display="flex">
            <Box sx={{ width: '200px', fontWeight: 'bold' }}>
              表示テキスト
              <FontAwesomeIcon icon={faCircleQuestion} className="px-2" />
              (任意)
            </Box>
            <TextField
              size="small"
              placeholder="このページを閲覧するにはパスワードを入力してください"
              value={newDescriptionText}
              onChange={handleDescriptionTextChange}
              helperText="テキストは255文字以下でご入力ください（入力した内容はプレビューでは表示されません。実際にパスワード保護された公開ページをご確認ください。）"
              variant="outlined"
              multiline
              rows={2}
              error={!!contextErrors?.auth_page_description}
            />
          </Box>
          <Box mt={4}>
            <Typography variant="caption" style={{ lineHeight: '0', marginTop: '20px' }}>
              ※パスワード保護が含まれているプランまたはオプションの契約が解除された場合、全ページのパスワード保護が解除されます。
              <br />
              ※パスワードの管理不足によるデータ流出や当機能の誤った使用、または第三者によって生じた損害に関しては当社は一切の責任を負いません。
              <br />
              ※パスワード保護が適用されているページを複製した場合、複製されたページのパスワード保護は解除されます。
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default PasswordProtection;
